import React, { useState, useEffect } from 'react';
import { Box, Text, Grid, FileInput, Card, Image, Stack, SimpleGrid } from '@mantine/core';
import { useTranslations } from 'next-intl';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface LabTestStepProps {
  hasLabTest: boolean;
  labTestFiles: File[];
  medicalReportFiles: File[];
  onHasLabTestChange: (checked: boolean) => void;
  onLabTestFilesChange: (files: File[]) => void;
  hasMedicalReport: boolean;
  onHasMedicalReportChange: (checked: boolean) => void;
  onMedicalReportFilesChange: (files: File[]) => void;
}

interface SelectionCardProps {
  value: string;
  label: string;
  description: string;
  image: string;
  onSelect: (value: string) => void;
  isSelected: boolean;
}

const SelectionCard: React.FC<SelectionCardProps> = ({
  value,
  label,
  description,
  image,
  onSelect,
  isSelected,
}) => {
  const t = useTranslations('labTestStep');

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={{
        cursor: 'pointer',
        backgroundColor: isSelected ? '#f0f0f0' : 'white',
        height: '100%',
      }}
      onClick={() => onSelect(value)}
    >
      <Card.Section>
        <Image src={image} height={160} alt={t(label)} />
      </Card.Section>

      <Stack mt="md">
        <Text>{t(label)}</Text>
        <Text size="sm" color="dimmed">
          {t(description)}
        </Text>
      </Stack>
    </Card>
  );
};

const labTestOptions = [
  {
    value: 'hasLabTest',
    label: 'hasLabTest.label',
    description: 'hasLabTest.description',
    image: '/images/labtest.png',
  },
  {
    value: 'noLabTest',
    label: 'noLabTest.label',
    description: 'noLabTest.description',
    image: '/images/nolabtest.png',
  },
];

const medicalReportOptions = [
  {
    value: 'hasMedicalReport',
    label: 'hasMedicalReport.label',
    description: 'hasMedicalReport.description',
    image: '/images/medicalreport.png',
  },
  {
    value: 'noMedicalReport',
    label: 'noMedicalReport.label',
    description: 'noMedicalReport.description',
    image: '/images/nomedicalreport.png',
  },
];

const LabTestStep: React.FC<LabTestStepProps> = ({
  hasLabTest,
  labTestFiles,
  medicalReportFiles,
  onHasLabTestChange,
  onLabTestFilesChange,
  hasMedicalReport,
  onHasMedicalReportChange,
  onMedicalReportFilesChange,
}) => {
  const t = useTranslations('labTestStep');
  const [filePreviews, setFilePreviews] = useState<{ [key: string]: string[] }>({
    labTest: [],
    medicalReport: [],
  });
  const isMobile = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    const generatePreviews = (files: File[], key: 'labTest' | 'medicalReport') => {
      const newPreviews = files.map((file) => URL.createObjectURL(file));
      setFilePreviews((prev) => ({ ...prev, [key]: newPreviews }));
    };

    generatePreviews(labTestFiles, 'labTest');
    generatePreviews(medicalReportFiles, 'medicalReport');

    return () => {
      // Clean up object URLs to avoid memory leaks
      filePreviews.labTest.forEach(URL.revokeObjectURL);
      filePreviews.medicalReport.forEach(URL.revokeObjectURL);
    };
  }, [labTestFiles, medicalReportFiles]);

  const handleFileChange = (files: File[] | null, type: 'labTest' | 'medicalReport') => {
    if (files) {
      if (type === 'labTest') {
        onLabTestFilesChange(files);
      } else {
        onMedicalReportFilesChange(files);
      }
    } else {
      // If files is null, set to an empty array
      if (type === 'labTest') {
        onLabTestFilesChange([]);
      } else {
        onMedicalReportFilesChange([]);
      }
    }
  };

  const handleOptionSelect = (value: string, type: 'labTest' | 'medicalReport') => {
    const newHasFile = value === `has${type.charAt(0).toUpperCase() + type.slice(1)}`;
    if (type === 'labTest') {
      onHasLabTestChange(newHasFile);
    } else {
      onHasMedicalReportChange(newHasFile);
    }
    if (!newHasFile) {
      handleFileChange([], type);
    }
  };

  const renderFileSection = (type: 'labTest' | 'medicalReport') => {
    const hasFile = type === 'labTest' ? hasLabTest : hasMedicalReport;
    const files = type === 'labTest' ? labTestFiles : medicalReportFiles;
    const previews = filePreviews[type];

    return (
      <>
        <Text size={isMobile ? 'md' : 'lg'} fw={500} mt="lg">
          {t(type === 'labTest' ? 'recentLabTests' : 'medicalReports')}
        </Text>
        <Grid gutter={isMobile ? 'xs' : 'md'}>
          {(type === 'labTest' ? labTestOptions : medicalReportOptions).map((option) => (
            <Grid.Col key={option.value} span={isMobile ? 12 : 6}>
              <SelectionCard
                {...option}
                onSelect={(value) => handleOptionSelect(value, type)}
                isSelected={
                  option.value === `has${type.charAt(0).toUpperCase() + type.slice(1)}`
                    ? hasFile
                    : !hasFile
                }
              />
            </Grid.Col>
          ))}
        </Grid>
        {hasFile && (
          <Box mt="md">
            <FileInput
              label={t(type === 'labTest' ? 'uploadLabTests' : 'uploadMedicalReport')}
              placeholder={t('chooseFiles')}
              onChange={(files) => handleFileChange(files, type)}
              accept=".pdf,.jpeg,.jpg,.png"
              multiple
            />
            {files.length > 0 && (
              <Box mt="md">
                <Text mb="xs">
                  {t(type === 'labTest' ? 'uploadedLabTests' : 'uploadedMedicalReports')}:
                </Text>
                <SimpleGrid cols={isMobile ? 2 : 4} spacing="xs">
                  {previews.map((preview, index) => (
                    <Card key={index} shadow="sm" padding="xs" radius="md" withBorder>
                      {files[index].type.startsWith('image/') ? (
                        <Image
                          src={preview}
                          alt={`${t(type === 'labTest' ? 'labTestPreview' : 'medicalReportPreview')} ${index + 1}`}
                          height={100}
                          fit="contain"
                        />
                      ) : (
                        <Box
                          style={{
                            height: 100,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Text size="sm">{files[index].name}</Text>
                        </Box>
                      )}
                    </Card>
                  ))}
                </SimpleGrid>
              </Box>
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <Box p={isMobile ? 'xs' : 'md'}>
      <Stack gap={isMobile ? 'xs' : 'md'}>
        {renderFileSection('labTest')}
        {renderFileSection('medicalReport')}
      </Stack>
    </Box>
  );
};

export default LabTestStep;
